/**
 * Login (DESIGN.md §4.2: keep, simplified copy; no rail).
 *
 * T642 M6 (R1b) rebuilt this screen. What was here until now: three fixed
 * buttons — Helen / Owen / Other — beside ONE shared passcode. Whoever held that
 * passcode could pick any of those names, so the value stamped on every quote,
 * rate edit and status change that session recorded was a CLAIM, not an
 * identity. The server stopped accepting it at M4; this is the screen catching
 * up. Now: a login name and that person's own password, both posted to
 * POST /api/login, and the name the rail then shows is read back from the server
 * (GET /api/me), never from what was typed here.
 *
 * ⛔ NO NAME BUTTONS MAY COME BACK, in any form. Two separate reasons:
 *   1. They published the roster — 'Helen','Owen','Other' shipped in the client
 *      bundle, which core/data/operators-repo.ts's roster note is careful never to turn
 *      into a live one.
 *   2. e2e/tests/smoke.spec.ts asserts that a BUTTON named 'Helen' is hidden
 *      after login, as the pin that the rail renders the operator as PLAIN TEXT
 *      (components/Rail.tsx:120). A name button on THIS screen would make that
 *      pin pass for the wrong reason.
 *
 * ⛔ AND THE PLACEHOLDER IS A TEST CONTRACT. e2e/fixtures/journey.ts uses the
 * password field DISAPPEARING as the readiness signal for all 33 logins in the
 * net. It keyed on 'Passcode' and now keys on 'Password'; a screen that keeps a
 * matching placeholder after login makes every spec hang or false-green, so the
 * two change together. The T937 labels below are therefore ADDITIVE: both
 * placeholders stay exactly as they are.
 *
 * ⛔ THE TWO <label>s ARE THE ACCESSIBLE NAME NOW, AND THERE IS NO aria-label
 * BEHIND THEM (T937). This screen had placeholder-only labelling, which
 * components/CallInCapture.tsx:105 forbids in the imperative -- "A REAL <label>,
 * never a placeholder (WCAG 3.3.2)" -- a Level A rule the codebase stated and
 * then broke here. The aria-labels were REMOVED rather than kept beside the
 * labels, deliberately: aria-label overrides a <label for>, so keeping both
 * would let a broken for/id association pass silently. shell.spec.tsx pins
 * getByLabelText for exactly that reason, and that pin only discriminates
 * BECAUSE there is no aria-label -- getByLabelText matches aria-label too, so
 * re-adding one would make the pin pass without any visible label at all.
 *
 * Net-locked strings kept VERBATIM: heading 'Quote Helper', button 'Enter'.
 * Failure copy is generic (SEC-5) and em-dash-free.
 *
 * ⭐⭐ T1749 M9 (DS-27) — THE SCREEN IS `Card` + `FormField` + `Button` AND DRAWS
 * NOTHING OF ITS OWN. Every law above survived the move rather than being
 * re-argued, and each one is enforced by the component now instead of by hand:
 *   · both PLACEHOLDERS are passed through, because the net's readiness signal is
 *     the password placeholder disappearing (`e2e/fixtures/journey.ts:39`, all 33
 *     UI logins). `FormField` takes `placeholder`, so nothing had to be dropped.
 *   · NO `aria-label` on either input: `FormField` renders a real
 *     `<label class="field-label" for={id}>` and has no aria-label prop at all,
 *     so the pin at `shell.spec.tsx:172-179` cannot be turned into a tautology by
 *     a future edit here. Exactly two labels, zero `input[aria-label]`.
 *   · the focus-restore keeps its own ref through `FormField`'s `elementRef`.
 * ⛔ THE ENTER HANDLER BECAME A `<form>`, AND THAT IS THE ONLY WAY IT COULD.
 * `FormField` has no `onKeyDown`, so the two `onEnterKey` handlers had nowhere to
 * live; a native submit is what the portal's three sign-in screens already use
 * and it is strictly better than the handler it replaces (it also answers the
 * keyboard's Go key and the password manager's fill-and-submit, which a
 * key listener on two inputs never did). The button is `type="submit"`.
 * ⛔ `p.login-error` STAYS OUTSIDE THE FIELDS, deliberately. It covers TWO things
 * neither field owns — the blank-field refusal (which is about the pair) and the
 * server's one generic refusal (which names no field, SEC-5) — so it is a
 * screen-level message with `aria-live`, not a per-field `error`. Moving it into
 * a `FormField` would have made it claim the password was the thing at fault.
 */
import { useRef, useState } from 'preact/hooks';
import { Wordmark, Card, FormField, Button } from '@ecmb/ui';

export type LoginProps = {
  /** Attempt a login; resolves to null on success, or the failure copy. */
  onLogin: (username: string, password: string) => Promise<string | null>;
};

export function Login({ onLogin }: LoginProps) {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const passwordRef = useRef<HTMLInputElement>(null);

  const submit = async () => {
    if (busy) return;
    // A blank field is refused HERE rather than spent on a round trip. It is not
    // a validation opinion about the credential (the server owns that, and says
    // one identical thing for every failure): an empty submit would burn one of
    // the operator's own failed-sign-in budget for a typo she can see.
    if (username.trim() === '' || password === '') {
      setError('Type your username and password to continue.');
      return;
    }
    setBusy(true);
    setError(null);
    const failure = await onLogin(username.trim(), password);
    setBusy(false);
    if (failure !== null) {
      setError(failure);
      // PUT THE CARET BACK (T642 M6 round B, UX finding 4). MEASURED: after a
      // failed sign-in `document.activeElement` was `<body>` — Enter is
      // `disabled` for the round trip and the browser blurs a disabled element,
      // so a keyboard-only operator had to Tab in from the top of the page to
      // retype. The password is the field she has to change (the username was
      // almost certainly right), and selecting it means the retype replaces
      // rather than appends. `aria-live` on the error still announces it.
      passwordRef.current?.focus();
      passwordRef.current?.select();
    }
  };

  const onSubmit = (e: Event) => {
    e.preventDefault();
    void submit();
  };

  return (
    <div class="login-wrap">
      <Card shape="cover">
        <p class="login-wordmark">
          <Wordmark />
        </p>
        <h1 class="login-title">Quote Helper</h1>
        <p class="login-hint">Sign in with your own account.</p>

        <form onSubmit={onSubmit}>
          <FormField
            id="login-username"
            label="Username"
            type="text"
            placeholder="Username"
            autoComplete="username"
            autocapitalize="none"
            spellcheck={false}
            value={username}
            onInput={setUsername}
          />

          <FormField
            id="login-password"
            label="Password"
            type="password"
            placeholder="Password"
            autoComplete="current-password"
            value={password}
            onInput={setPassword}
            elementRef={passwordRef}
          />

          <Button variant="primary" size="full" type="submit" disabled={busy}>
            Enter
          </Button>
        </form>

        <p class="login-error" aria-live="polite">{error}</p>
      </Card>
    </div>
  );
}
